import AIButton from '@cobliteam/ai/src/components/AIButton/AIButton';
import { getHighlightFromFeedItem } from '@cobliteam/ai/src/components/RiskRecognitionFloatingCard/feedHighlight';
import { formatRiskRecognitionCardDisplay, RISK_RULE_ICON } from '@cobliteam/ai/src/components/RiskRecognitionFloatingCard/formatRiskRecognitionCardDisplay';
import RiskHighlightText, { renderRiskHighlight } from '@cobliteam/ai/src/components/RiskRecognitionFloatingCard/RiskHighlightText';
import { useTranslation } from '@cobliteam/i18n';
import Badge from '@cobliteam/ui/components/Badge';
import Button from '@cobliteam/ui/components/Button';
import Divider from '@cobliteam/ui/components/Divider';
import Popover, { PopoverContent, PopoverTrigger } from '@cobliteam/ui/components/Popover';
import Skeleton from '@cobliteam/ui/components/Skeleton';
import Tabs, { TabsList, TabsTrigger } from '@cobliteam/ui/components/Tabs';
import Tooltip from '@cobliteam/ui/components/Tooltip';
import Typography from '@cobliteam/ui/components/Typography';
import Icon, { type IconNames } from '@cobliteam/ui/icons';
import { cn } from '@cobliteam/ui/utils';
import { formatDateTZ } from '@cobliteam/utils/date';
import {
  type ActionButton,
  type ButtonSetContentBlock,
  type FeedClientOptions,
  type FeedItem,
  type MarkdownContentBlock,
  NetworkStatus,
  type TextContentBlock,
} from '@knocklabs/client';
import { useKnockFeed } from '@knocklabs/react';
import DOMPurify from 'dompurify';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';

/**
 * Sino do feed in-app do header, com backend no Knock.
 *
 * Usa a camada headless do `@knocklabs/react`: o `KnockFeedProvider` continua
 * fornecendo o feed client/store, mas a UI é renderizada com o design system da Cobli.
 */
type FeedTab = 'all' | 'unread' | 'archived';
type BlockByName = Record<string, FeedItem['blocks'][number]>;

// `source.key` do workflow Knock → título canônico + ícone do tipo de risco. Mesma fonte (título e ícone) do
// ROW_LABELS em modules/account/.../KnockPreferences (namespace `account`), pra feed e preferências ficarem em
// sincronia. Inclui o typo `jorney` que chega do backend.
const RISK_META: Record<string, { titleKey: string; iconName: IconNames }> = {
  'fatigue-risk-recognition': { titleKey: 'notifications_center.knock.rows.fatigue_risk_recognition.title', iconName: 'coffee' },
  'speed-event-risk-recognition': { titleKey: 'notifications_center.knock.rows.speed_event_risk_recognition.title', iconName: 'gauge' },
  'long-journey-risk-recognition': { titleKey: 'notifications_center.knock.rows.long_journey_risk_recognition.title', iconName: 'clock' },

  // deprecated - dispatch some events wrong when it was called "jorney"
  'long-jorney-risk-recognition': { titleKey: 'notifications_center.knock.rows.long_journey_risk_recognition.title', iconName: 'clock' },
};

const getFeedOptionsForTab = (tab: FeedTab): FeedClientOptions => {
  if (tab === 'archived') return { status: 'all', archived: 'only' };
  return { status: tab === 'unread' ? 'unread' : 'all', archived: 'exclude' };
};

const maybeNavigateToUrlWithDelay = (url?: string) => {
  if (!url) return;
  // Adia a navegação para dar tempo do `markAsInteracted` (disparado logo antes do clique)
  // ser enviado ao Knock. `window.location.assign` é síncrono e destrói o contexto JS na hora,
  // cancelando a request de interação em voo; o atraso garante essa ordem (interação → redirect).
  setTimeout(() => window.location.assign(url), 200);
};

const getBlocksByName = (item: FeedItem): BlockByName =>
  item.blocks.reduce<BlockByName>((acc, block) => {
    acc[block.name] = block;
    return acc;
  }, {});

const getBodyHtml = (blocksByName: BlockByName) => (blocksByName.body as MarkdownContentBlock | TextContentBlock | undefined)?.rendered;

// Sem o literal `'de'` (pt-BR) pra não vazar português no locale en-US do switcher. `formatDateTZ` já
// resolve o nome do mês pelo locale default do app; só não embutimos palavra fixa no pattern.
const formatInsertedAt = (insertedAt: string) => formatDateTZ(insertedAt, 'dd MMM, HH:mm');

type KnockFeedItemProps = {
  item: FeedItem;
  isArchivedView: boolean;
  onArchive: (item: FeedItem) => void;
  onButtonAction: (item: FeedItem, action: ActionButton) => void;
  onUnarchive: (item: FeedItem) => void;
};

type KnockFeedItemContentProps = {
  isRisk: boolean;
  headline?: string;
  description?: string;
  rawBodyHtml?: string;
};

// Conteúdo textual do item: headline/descrição estruturados (`data`), com fallback no body markdown.
// Itens de risco herdam o tratamento do card canônico (headline em danger + ênfase/normalização via
// `renderRiskHighlight`). Notificações genéricas usam tinta neutra e texto literal — sem forçar o
// caixa-baixo do formatter de risco, que destruiria o título original.
const KnockFeedItemContent = ({ isRisk, headline, description, rawBodyHtml }: KnockFeedItemContentProps) => {
  const { t } = useTranslation('app-header');

  if (headline || description) {
    return (
      <>
        <Typography variant='description-title' className={cn('wrap-anywhere mt-2 leading-135', isRisk ? 'text-danger-10' : 'text-gray-12')}>
          {isRisk ? renderRiskHighlight(headline ?? '', 'font-semibold') : headline}
        </Typography>
        {description ? (
          isRisk ? (
            <RiskHighlightText text={description} variant='text-sm' className='wrap-anywhere text-gray-11 leading-5' />
          ) : (
            <Typography as='p' variant='text-sm' className='wrap-anywhere text-gray-11 leading-5'>
              {description}
            </Typography>
          )
        ) : null}
      </>
    );
  }

  if (rawBodyHtml) {
    return (
      <span
        // biome-ignore lint/security/noDangerouslySetInnerHtml: Knock feed body HTML is sanitized with DOMPurify before injection.
        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(rawBodyHtml) }}
      />
    );
  }

  return (
    <Typography variant='text-sm' color='secondary'>
      {t('feed.item.no_content')}
    </Typography>
  );
};

const KnockFeedItem = ({ item, isArchivedView, onArchive, onButtonAction, onUnarchive }: KnockFeedItemProps) => {
  const { t } = useTranslation(['app-header', 'account']);
  const blocksByName = useMemo(() => getBlocksByName(item), [item]);
  const rawBodyHtml = getBodyHtml(blocksByName);
  const buttonSet = blocksByName.actions as ButtonSetContentBlock | undefined;
  const actionButtons = buttonSet?.buttons ?? [];
  const itemData = item.data as { title?: string; description?: string } | null | undefined;
  const sourceKey = (item.source as { key?: string } | null | undefined)?.key;
  // `endsWith` em vez de `includes`: casa só os workflows `*-risk-recognition` reais (todos os RISK_META
  // terminam assim), sem capturar tipos futuros que contenham o trecho no meio (ex: `*-risk-recognition-tutorial`).
  const isRisk = Boolean(sourceKey?.endsWith('-risk-recognition'));
  const riskMeta = sourceKey ? RISK_META[sourceKey] : undefined;

  // Itens de risk-recognition carregam o highlight completo em `data`. Derivamos headline/descrição com o
  // MESMO formatter do card flutuante (formatRiskRecognitionCardDisplay), pra feed e card nunca divergirem
  // (headline = fator principal, ex: "Olhos fechados ao volante"). Sem highlight, caímos no `data` estruturado.
  const highlight = useMemo(() => getHighlightFromFeedItem(item), [item]);
  const cardDisplay = highlight ? formatRiskRecognitionCardDisplay(highlight) : null;

  // Ícone segue a regra da detecção principal (mesma do headline), não o workflow (`source.key`). Sem
  // highlight, cai no ícone do tipo canônico; sem mapeamento (tipo novo), no genérico.
  const ruleName = highlight?.metadata?.rule_name;
  const iconName: IconNames = (ruleName ? RISK_RULE_ICON[ruleName] : undefined) ?? riskMeta?.iconName ?? 'message-square-warning';
  const headline = (cardDisplay?.headline ?? (riskMeta ? t(riskMeta.titleKey, { ns: 'account' }) : itemData?.title))?.trim();
  const description = (cardDisplay?.description ?? itemData?.description)?.trim();

  const handleArchiveClick = () => {
    if (isArchivedView) {
      onUnarchive(item);
      return;
    }

    onArchive(item);
  };

  // A linha não é clicável: as ações ficam só nos botões. Mantemos `focus-within` (destaca a linha quando um
  // botão interno recebe foco por teclado), sem `hover` de linha — que sugeriria uma área clicável inexistente.
  return (
    <div className='flex gap-3 px-6 py-4 transition-colors focus-within:bg-gray-2'>
      <span className={cn('mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-full', isRisk ? 'bg-danger-3 text-danger-9' : 'bg-gray-3 text-gray-10')} aria-hidden>
        <Icon name={iconName} size='sm' />
      </span>

      <div className='min-w-0 flex-1 space-y-3'>
        <KnockFeedItemContent isRisk={isRisk} headline={headline} description={description} rawBodyHtml={rawBodyHtml} />

        <div className='flex flex-col gap-3'>
          <Typography as='span' variant='caption-xs' color='secondary'>
            {formatInsertedAt(item.inserted_at)}
          </Typography>

          <div className='flex flex-wrap items-center gap-2'>
            {actionButtons.map((button, index) =>
              index === 0 ? (
                <AIButton
                  key={button.name}
                  variant='ai-outline'
                  icon={false}
                  shape='cta'
                  size='sm'
                  showTooltip={false}
                  label={button.label}
                  onClick={() => onButtonAction(item, button)}
                />
              ) : (
                <Button key={button.name} type='button' size='sm' variant='ghost' color='primary' onClick={() => onButtonAction(item, button)}>
                  {button.label}
                </Button>
              )
            )}

            <Button type='button' size='sm' variant='ghost' color='primary' onClick={handleArchiveClick}>
              {isArchivedView ? t('feed.actions.unarchive') : t('feed.actions.archive')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Skeleton de carregamento que espelha a geometria do item (avatar + texto + ações), pra superfície não
// "pular" quando os itens chegam. Skeleton em vez de spinner é a convenção do design system pra conteúdo.
const KnockFeedItemSkeleton = () => (
  <div className='flex gap-3 px-6 py-4'>
    <Skeleton className='mt-0.5 size-9 shrink-0 rounded-full' />
    <div className='min-w-0 flex-1 space-y-3'>
      <div className='space-y-2'>
        <Skeleton className='h-4 w-3/5' />
        <Skeleton className='h-3 w-full' />
        <Skeleton className='h-3 w-4/5' />
      </div>
      <Skeleton className='h-3 w-24' />
      <Skeleton className='h-8 w-32 rounded-lg' />
    </div>
  </div>
);

const KnockFeedButton = () => {
  const { t } = useTranslation('app-header');
  const { feedClient, useFeedStore } = useKnockFeed();
  const history = useHistory();
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<FeedTab>('all');
  const items = useFeedStore((state) => state.items);
  const metadata = useFeedStore((state) => state.metadata);
  const networkStatus = useFeedStore((state) => state.networkStatus);
  const pageInfo = useFeedStore((state) => state.pageInfo);

  const isLoading = networkStatus === NetworkStatus.loading;
  const isFetchingMore = networkStatus === NetworkStatus.fetchMore;
  const isError = networkStatus === NetworkStatus.error;
  const isArchivedView = activeTab === 'archived';
  const unreadCount = metadata.unread_count;
  const unseenCount = metadata.unseen_count;
  // Badge mostra a quantidade de não lidas; vira um dot quando todas já foram vistas (mas seguem não lidas).
  const hasUnread = unreadCount > 0;
  const showUnreadDot = hasUnread && unseenCount === 0;

  useEffect(() => {
    if (!isOpen) return;
    void feedClient.fetch(getFeedOptionsForTab(activeTab));
  }, [activeTab, feedClient, isOpen]);

  useEffect(() => {
    if (!(isOpen && metadata.unseen_count > 0)) return;
    void feedClient.markAllAsSeen();
  }, [feedClient, isOpen, metadata.unseen_count]);

  // Resolve a `action` do Knock pelo formato:
  // - começa com `?` → query params: atualiza só a search da rota atual pelo router (sem reload).
  // - URL http(s) completa → redirect de página inteira (delay garante interação → redirect).
  // - caminho relativo (`/algo`) → navega pelo router, sem reload.
  // Em deep links que abrem um drawer (param com sentinela `true`), troca o valor pelo id do item do
  // feed, pra superfície destino resolver e exibir o evento certo.
  const navigateToAction = (url: string | undefined, item: FeedItem) => {
    if (!url) return;
    if (url.startsWith('?')) {
      const params = new URLSearchParams(url);
      for (const [key, value] of params) {
        if (value === 'true') params.set(key, item.id);
      }
      history.push({ search: `?${params.toString()}` });
      return;
    }
    if (/^https?:\/\//.test(url)) {
      maybeNavigateToUrlWithDelay(url);
      return;
    }
    history.push(url);
  };

  const handleButtonAction = (item: FeedItem, action: ActionButton) => {
    void feedClient.markAsInteracted(item, {
      action: action.action,
      label: action.label,
      name: action.name,
      type: 'button_click',
    });
    setIsOpen(false);
    navigateToAction(action.action, item);
  };

  const handleLoadMore = () => {
    void feedClient.fetchNextPage(getFeedOptionsForTab(activeTab));
  };

  const handleOpenSettings = () => {
    setIsOpen(false);
    // `?section=ia-avisos` ancora na seção "IA - Avisos inteligentes" da página (KnockPreferences a rola
    // para a viewport ao montar). Query param, não hash — o roteador trata `#...` como rota.
    history.push({ pathname: '/config/notifications-center', search: '?section=ia-avisos' });
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <Tooltip content={t('feed.tooltip')} position='right' asChild>
        <PopoverTrigger asChild>
          <div className='relative'>
            <Badge
              variant={showUnreadDot ? 'dot' : 'default'}
              content={showUnreadDot ? undefined : unreadCount}
              max={99}
              hide={!hasUnread}
              position='top-right'
              color='danger'
              size='xs'
            >
              <AIButton
                variant='ai-outline'
                data-testid='app-header-knock-feed-button'
                size='sm'
                icon={<Icon name='message-square-warning' size='sm' />}
                aria-label={t('feed.tooltip')}
              />
            </Badge>
          </div>
        </PopoverTrigger>
      </Tooltip>

      <PopoverContent
        align='end'
        side='right'
        showArrow={false}
        // Não move o foco pra dentro ao abrir: senão o foco cai no 1º botão (engrenagem) e o tooltip dele
        // abre via foco e fica preso. Sem auto-focus, o tooltip só aparece no hover / tab pelo teclado.
        onOpenAutoFocus={(event) => event.preventDefault()}
        className='flex w-[440px] max-w-[calc(100vw-1rem)] flex-col overflow-hidden rounded-xl border-gray-6 p-0 shadow-xl'
      >
        <div className='flex items-center justify-between gap-3 border-gray-5 border-b py-3 pr-3 pl-6'>
          <Typography as='h2' variant='heading-lg'>
            {t('feed.title')}
          </Typography>
          <div className='flex items-center gap-1'>
            <Tooltip content={t('feed.actions.settings')} position='bottom' asChild>
              <div>
                <Button
                  type='button'
                  size='sm'
                  variant='ghost'
                  color='default'
                  icon={<Icon name='settings' />}
                  aria-label={t('feed.actions.settings')}
                  onClick={handleOpenSettings}
                />
              </div>
            </Tooltip>
            <Button type='button' size='sm' variant='ghost' color='default' icon={<Icon name='x' />} aria-label={t('feed.actions.close')} onClick={() => setIsOpen(false)} />
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as FeedTab)}>
          <TabsList variant='line'>
            <TabsTrigger value='all' size='md'>
              {t('feed.tabs.all')}
            </TabsTrigger>
            <TabsTrigger value='archived' size='md'>
              {t('feed.tabs.archived')}
            </TabsTrigger>
          </TabsList>
        </Tabs>

        <div className='max-h-[min(560px,calc(100vh-8rem))] overflow-y-auto'>
          {isLoading && (
            <div role='status' aria-label={t('feed.states.loading')}>
              {[0, 1, 2].map((index) => (
                <Fragment key={index}>
                  {index > 0 && <Divider />}
                  <KnockFeedItemSkeleton />
                </Fragment>
              ))}
            </div>
          )}

          {isError && !isLoading && (
            <div className='flex flex-col items-center gap-2 px-6 py-10 text-center'>
              <Icon name='triangle-alert' className='text-danger' aria-hidden />
              <Typography variant='text-sm-medium'>{t('feed.states.error_title')}</Typography>
              <Typography variant='caption-xs' color='secondary'>
                {t('feed.states.error_description')}
              </Typography>
            </div>
          )}

          {!(isLoading || isError) && items.length === 0 && (
            <div className='flex flex-col items-center gap-2 px-6 py-10 text-center'>
              <div className='flex size-10 items-center justify-center rounded-full bg-gray-3 text-gray-10'>
                <Icon name='bell-off' aria-hidden />
              </div>
              <Typography variant='text-sm-medium'>{t('feed.states.empty_title')}</Typography>
              <Typography variant='caption-xs' color='secondary'>
                {t('feed.states.empty_description')}
              </Typography>
            </div>
          )}

          {!(isLoading || isError) &&
            items.map((item, index) => (
              <Fragment key={item.id}>
                {index > 0 && <Divider />}
                <KnockFeedItem
                  item={item}
                  isArchivedView={isArchivedView}
                  onArchive={(feedItem) => void feedClient.markAsArchived(feedItem)}
                  onButtonAction={handleButtonAction}
                  onUnarchive={(feedItem) => void feedClient.markAsUnarchived(feedItem)}
                />
              </Fragment>
            ))}

          {!(isLoading || isError) && pageInfo.after && (
            <div className='flex justify-center px-4 py-3'>
              <Button type='button' size='sm' variant='outlined' color='default' loading={isFetchingMore} onClick={handleLoadMore}>
                {t('feed.actions.load_more')}
              </Button>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default KnockFeedButton;
